import { getMeta, type GkDb } from '../sync/db.ts'
import type { SyncEngine } from '../sync/engine.ts'
import { TransportError, type Transport } from '../sync/transport.ts'
import { attachReporter, capture, DiagnosticsReporter, localStore, type DiagEntry, type DiagStore, type SendOutcome } from './reporter.ts'

/** What the server answers (api.report_client_diagnostic): stored, or why not. */
interface ReportAnswer { stored: boolean; reason?: 'DUPLICATE' | 'TOO_OLD' | 'RATE_LIMITED' | 'DEVICE_PENDING' }

/** One report to the server. Its instant is the device's clock corrected by the offset the engine measured (as a command's is). A network failure, a frozen API, a
 *  lapsed sign-in, or a device the server could not register yet (DEVICE_PENDING: it registers a device it does not know, as the heartbeat does, unless the club is busy)
 *  keeps it for later; anything else the server refuses is dropped (a refusal is never retried). */
export function serverSender(o: { transport: Transport; db: GkDb; orgId: string; deviceId: string }): (e: DiagEntry) => Promise<SendOutcome> {
  return async (e) => {
    const offset = (await getMeta<number>(o.db, 'clockOffsetMs').catch(() => 0)) ?? 0
    try {
      const r = await o.transport.rpc<ReportAnswer>('report_client_diagnostic', {
        p_org: o.orgId, p_code: e.code, p_message: e.message, p_app_version: e.app_version, p_device_id: o.deviceId,
        p_at: new Date(e.at - offset).toISOString(), p_id: e.id,
      })
      return r?.stored ? 'stored' : r?.reason === 'RATE_LIMITED' ? 'pause' : r?.reason === 'DEVICE_PENDING' ? 'retry' : 'dropped'
    } catch (err) {
      if (err instanceof TransportError && ['network', 'timeout', 'frozen', 'auth'].includes(err.kind)) return 'retry'
      return 'dropped'
    }
  }
}

/** The sync engine's own errors, as the engine meets them: the transport it is given is this one, which reports a failed push, pull, timeout report or heartbeat by its kind
 *  (SYNC_SERVER, SYNC_PROTOCOL, SYNC_TIMEOUT) and hands the error on unchanged. The network being away and a frozen API (a restore) are not reported: the field works
 *  offline by design, and a lapsed sign-in is the engine's to handle. The other calls (the office reads, the kiosk) pass untouched. */
const WATCHED = new Set<PropertyKey>(['submit', 'pull', 'markTimeout', 'heartbeat'])
const REPORTED: Partial<Record<TransportError['kind'], string>> = { server: 'SYNC_SERVER', protocol: 'SYNC_PROTOCOL', timeout: 'SYNC_TIMEOUT' }
export function watchTransport(t: Transport, report: (code: string, message: unknown) => void = capture): Transport {
  return new Proxy(t, {
    get(target, prop) {
      const v = Reflect.get(target, prop, target) as unknown
      if (typeof v !== 'function') return v
      const fn = v as (...a: unknown[]) => unknown
      if (!WATCHED.has(prop)) return fn.bind(target)
      return async (...args: unknown[]) => {
        try { return await fn.apply(target, args) } catch (e) {
          const code = e instanceof TransportError ? REPORTED[e.kind] : 'SYNC_SERVER'
          if (code) { try { report(code, `${String(prop)}: ${e instanceof Error ? e.message : String(e)}`) } catch { /* a reporter never throws */ } }
          throw e
        }
      }
    },
  })
}

/** The reporter of one login and club, wired (ClubProvider): it takes the window's errors and the sync engine's (watchTransport), with what was captured under the same
 *  login and club before it started, and sends what is queued after every successful sync, and a few seconds after a report unless the device is known to be offline
 *  (a first sync that failed at the server still sends: the report registers the device). Returns the function that lets it go. */
export function startDiagnostics(o: {
  engine: SyncEngine; transport: Transport; db: GkDb; userId: string; orgId: string; deviceId: string; appVersion: string; storageKey: string
  store?: DiagStore; flushDelayMs?: number
}): () => void {
  const reporter = new DiagnosticsReporter(o.store ?? localStore(`gk.diag.${o.storageKey}`), o.appVersion)
  const toServer = serverSender(o)
  let timer: ReturnType<typeof setTimeout> | undefined
  let stopped = false
  // once let go (a sign-out, another login or club), a flush still running sends nothing more: the rest stays in this login and club's queue, never goes under the
  // next session's sign-in
  const send = (e: DiagEntry): Promise<SendOutcome> => (stopped ? Promise.resolve('retry') : toServer(e))
  const flush = () => { if (!stopped && reporter.pending().length) void reporter.flush(send) }
  const soon = () => {
    if (stopped || o.engine.state.online === false) return
    clearTimeout(timer)
    timer = setTimeout(flush, o.flushDelayMs ?? 3_000)
  }
  let lastSyncAt = o.engine.state.lastSyncAt
  const unsubscribe = o.engine.subscribe(() => {
    const s = o.engine.state
    if (s.lastSyncAt !== null && s.lastSyncAt !== lastSyncAt) { lastSyncAt = s.lastSyncAt; flush() }
  })
  const detach = attachReporter(reporter, { userId: o.userId, orgId: o.orgId }, soon)
  return () => { stopped = true; clearTimeout(timer); unsubscribe(); detach() }
}
